"use client";
import { PopupButton } from "react-calendly";

const pilotSteps = [
  {
    week: "Week 1–2",
    title: "Audit & publish",
    desc: "We map your visibility across 9 AI engines and publish the first pages under your domain, targeting your buyers' questions.",
  },
  {
    week: "Day 30",
    title: "Visibility lift",
    desc: "Your pages are live and getting cited, and attribution is wired. You see which engines are picking you up. This is where the 30-day guarantee is measured.",
  },
  {
    week: "Day 60–90",
    title: "Enquiries & review",
    desc: "Attributed enquiries build as buyers find your pages. We review enquiry count, attribution, and pipeline together, and you decide on continuation.",
  },
];

const pilotDeliverables = [
  "9-engine baseline visibility audit",
  "~15–20 GEO pages published on your own domain",
  "Attribution wired to every enquiry: which engine, which page",
  "Off-page action list (your #1 citation lever)",
  "Reviews at day 30 (visibility lift) and day 60 (enquiries + attribution)",
];

/** Small check glyph that inherits color via currentColor. */
function Check({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}

/**
 * Founding-partner pilot block. Teaser by default (homepage, /lead-generation).
 * Pass `detailed` (on /pilot) to also show the founding price, the "what's
 * included" deliverables, and the 30-day guarantee.
 */
export default function DeRiskSection({ detailed = false }: { detailed?: boolean }) {
  return (
    <section className="w-full bg-surface-raised py-14 sm:py-20 lg:py-24">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">

          {/* Left: founding-partner framing */}
          <div>
            <div className="inline-flex items-center px-3 py-1.5 bg-surface border border-hairline rounded-full text-[11px] font-bold tracking-widest text-ink-muted mb-6 uppercase">
              Founding Partner Programme
            </div>
            <h2 className="font-display text-[28px] sm:text-4xl font-black text-ink leading-tight tracking-[-0.02em] mb-4">
              5 pilot spots for mid-market B2B this quarter
            </h2>

            {detailed && (
              <div className="mb-5">
                <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1">
                  <span className="font-body text-ink-faint line-through text-xl tabular-nums">$2,000</span>
                  <span className="font-display font-black text-ink text-4xl sm:text-5xl tabular-nums">$1,200</span>
                  <span className="text-ink-muted font-semibold">one-time</span>
                </div>
                <p className="mt-1.5 text-[14px] font-semibold text-accent">
                  Founding Partner rate · 40% off · 5 spots this quarter
                </p>
              </div>
            )}

            <p className="text-[16px] text-ink-muted leading-relaxed mb-6">
              Fixed engagement. Direct founder access. Results reviewed together at day&nbsp;60. Continuation and exit terms are agreed before the pilot starts.
            </p>

            {detailed && (
              <div className="mb-6 flex items-start gap-3 rounded-2xl bg-accent/10 border border-accent/20 p-4">
                <span className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-full bg-accent text-on-accent flex items-center justify-center">
                  <Check className="w-3.5 h-3.5" />
                </span>
                <p className="text-[14px] text-ink leading-relaxed">
                  <span className="font-bold">30-day guarantee.</span>{" "}
                  A measurable lift in your AI-search visibility and citations within 30 days, or your pilot fee back.
                </p>
              </div>
            )}

            <p className="text-[14px] text-ink-faint leading-relaxed mb-8">
              Your lead data is yours and exportable. Pages appear on your domain but remain a TopGeo-hosted, managed service delivered through our edge.
            </p>
            <PopupButton
              url="https://calendly.com/topgeo-sales/30min"
              rootElement={typeof document !== "undefined" ? document.body : undefined}
              text="Book an Audit Call"
              className="inline-flex items-center justify-center px-6 py-3.5 bg-ink text-surface font-semibold text-[15px] rounded-lg hover:opacity-90 active:opacity-80 transition-opacity cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
            />
          </div>

          {/* Right: pilot timeline */}
          <div className="flex flex-col gap-4">
            {pilotSteps.map((step) => (
              <div key={step.week} className="bg-surface border border-hairline rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <span className="font-mono text-xs font-bold text-ink-faint border border-hairline rounded-full px-2.5 py-1 flex-shrink-0 mt-0.5">
                    {step.week}
                  </span>
                  <div>
                    <p className="font-bold text-ink mb-1">{step.title}</p>
                    <p className="text-[14px] text-ink-muted leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>

        {detailed && (
          <div className="mt-10 lg:mt-14 border-t border-hairline pt-8">
            <p className="font-display font-bold text-lg text-ink mb-5">
              What&apos;s included in your 60 days
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3.5">
              {pilotDeliverables.map((d) => (
                <li key={d} className="flex items-start gap-3">
                  <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-accent/10 text-accent flex items-center justify-center">
                    <Check className="w-3 h-3" />
                  </span>
                  <span className="text-[14px] text-ink-muted leading-relaxed">{d}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

      </div>
    </section>
  );
}
